import axios, { AxiosInstance } from 'axios'
import { cortexStreamUrl } from '../lib/cortexUrl'
import { apiOrigin, getRuntimeConfig } from '../lib/runtimeConfig'
import type { CreateTenantUserResponse } from '../utils/memberInvite'
import type { TToken, TTokenCreateRequest } from '@it-sce/ui'

// TQ #7348 (Bundle #7343): Mode-aware Endpoint-Helper für Personal-vs-Org-Paare
// mit identischem Suffix (/api/me/<x> ↔ /api/org/<slug>/<x>). Kanonisch in
// utils/cockpitMode definiert, hier re-exportiert damit Pages ihn neben dem
// `api`-Client aus services/api importieren können.
export { modeAwareEndpoint, isPersonalMode } from '../utils/cockpitMode'
export type { OrgRef } from '../utils/cockpitMode'


export interface CortexChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface CortexChatResponse {
  response: string
  session_id: string
  tokens_used?: number
}

// TQ #8466 (DSGVO / cortex PR #107): per-tenant Privacy-Tier. 'local' ist ein
// interner Routing-Wert und wird der Kundin NIE als Wahl angeboten.
export type PrivacyMode = 'eu_only' | 'us_ok'

// TQ #13061: Kunden-gesteuerte Verarbeitungs-Präferenz (3-Stufen-Wahl).
// Monotone Leiter: lokal < EU < EU+US — Obergrenze nur verschärfen, nie aufweichen.
export type ProcessingPreference = 'nur_lokal' | 'nur_souveraen_eu' | 'eu_plus_us_vertraglich'

export interface PrivacyModeInfo {
  privacy_mode: PrivacyMode
  privacy_confirmed: boolean
  privacy_confirmed_at: string | null
  processing_preference?: ProcessingPreference
}

// TQ #8277/#8278: Onboarding-State des eingeloggten Users fuer den
// proaktiven Scout-Auto-Open (GET /api/me/onboarding-state).
export type OnboardingStage = 'new_no_booking' | 'booked' | 'acked'

export interface OnboardingState {
  onboarding_stage: OnboardingStage
  service_count: number
  acked: boolean
  auto_open_shown: boolean
}

/**
 * TQ #6093: typed events from /api/v1/chat/stream WebSocket. Server-Schema
 * (WsEvent enum in cortex-server/src/sinks/websocket.rs + stream.rs).
 * `info` und `session_info` werden direkt aus stream.rs gesendet (session-setup),
 * der Rest aus dem WebSocketSink waehrend der Agent-Loop.
 */
export type CortexStreamEvent =
  | { type: 'session'; session_id: string; model: string }
  | { type: 'info'; message: string }
  | { type: 'thinking'; text: string }
  | { type: 'content'; text: string }
  | { type: 'tool_start'; tool_name: string; args: unknown }
  | { type: 'tool_result'; tool_name: string; result: string; is_error: boolean }
  // TQ #6981 (Confirm-Engine P3): Cortex signalisiert, dass ein WRITE-Tool
  // eine explizite Bestaetigung des Users braucht (standing_permission
  // permission_type=confirm). Der Tool-Call ist serverseitig geparkt bis
  // /confirmations/{confirm_id}/approve oder /cancel aufgerufen wird.
  | { type: 'confirmation_required'; tool: string; reason: string; instruction: string; confirm_id: string }
  | { type: 'done'; finish_reason: string }
  | { type: 'error'; message: string }

// TQ #6981: Antwort des approve-Endpoints — der nachgeholte Tool-Result.
export interface CortexConfirmationResult {
  is_error: boolean
  content: string
  tool: string
}

// TQ #6984 (Confirm-Engine P4): Standing-Permission, wie sie der Cortex
// Admin-Grant-Endpoint zurueckgibt. permission_type=confirm bewirkt die
// Confirm-Card (P3), allow/deny sind harte Regeln.
export type CortexPermissionType = 'allow' | 'deny' | 'confirm'
export type CortexPermissionScope = 'tool' | 'server' | 'category'

export interface CortexStandingPermission {
  id: string
  user_id: string
  permission_type: CortexPermissionType
  scope: CortexPermissionScope
  target: string
  granted_at: string
  expires_at?: string | null
  granted_via?: string | null
}

export interface CortexGrantInput {
  permission_type: CortexPermissionType
  scope: CortexPermissionScope
  target: string
  constraints?: Record<string, unknown>
  expires_at?: string | null
}

export interface CortexSessionMessage {
  id?: string
  session_id?: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  created_at?: string
}

export interface CortexSession {
  id: string
  user_id: string
  tenant_id: string
  title: string
  model: string
  agent_id?: string | null
  created_at: string
  updated_at: string
  last_message_at: string
  archived: boolean
}

export interface CortexModelInfo {
  id: string
  name: string
  provider: 'ollama' | 'anthropic' | 'mistral' | string
  context_length?: number | null
}

// TQ #7975: Tenant-Switcher Typen (GET /api/me/tenants).
export interface TenantItem {
  kind: 'personal' | 'org'
  name: string
  slug?: string
  public_token?: string
  domain?: string
  keycloak_realm?: string
  role: string
  cockpit_url: string
}

export interface MyTenantsResponse {
  success: boolean
  current_slug: string
  tenants: TenantItem[]
}

/// Aria Token-Quota Response (TQ #5763) — siehe org_aria_quota.go.
export interface AriaTopupItem {
  id: number
  tokens: number
  tokens_consumed: number
  tokens_remaining: number
  kind: 'one_time' | 'recurring'
  package_label: string
  price_eur: number
  purchased_at: string
  period_resets_at: string
  expires_at: string
}

export interface AriaTopupPackage {
  id: string
  name: string
  description: string
  sonnet_tokens: number
  price_eur: number
  price_label: string
}

// Token-Bounty Claim-Code-Einlösung (TQ #8686 / #8672). Antwort von Cortex
// POST /api/v1/me/bounty/claim bei Erfolg.
export interface BountyClaimResult {
  claimed: boolean
  tokens: number
  severity: string
}

// TQ #5711: Org-scoped Service-Health (GET /api/org/:slug/health).
export type OrgHealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown' | 'stopped'

export interface OrgHealthEntry {
  slug: string
  name: string
  domain?: string
  url?: string
  status: OrgHealthStatus
  last_checked: string
  response_ms: number
  error?: string
}

export interface OrgHealthResponse {
  customer?: { email?: string }
  counts: {
    total: number
    healthy: number
    degraded: number
    unhealthy: number
    unknown: number
  }
  overall: 'healthy' | 'degraded' | 'unhealthy' | ''
  services: OrgHealthEntry[]
  checked: string
}

export interface AriaQuotaResponse {
  success: boolean
  tenant: string
  configured?: boolean
  budget_mode: 'hard' | 'soft'
  managed: boolean
  limit: {
    base_tokens: number
    topup_remaining: number
    effective: number
    welcome_bonus_active: boolean
    welcome_bonus_tokens: number
  }
  usage: {
    current_month_sonnet: number
    current_month_sonnet_in?: number
    current_month_sonnet_out?: number
    current_month_haiku: number
    current_month_opus: number
    remaining_tokens: number
    period_start: string
    period_resets_at: string
  }
  topups: {
    total_purchased: number
    consumed: number
    remaining: number
    items: AriaTopupItem[]
  }
  available_topup_packages: AriaTopupPackage[]
}

// Token-Bounty Ledger (TQ #8684 / #8673, ZIELBILD-Token-Bounty §8.6) — die
// bestätigten Bounty-Token-Gutschriften des eingeloggten Users. Cortex liefert
// die Form fertig, portal-api reicht sie 1:1 durch (GET /api/me/bounty-history).
export interface BountyLedgerEntry {
  date: string // RFC3339
  severity: string // major | minor | critical | … (aus BOUNTY-<LABEL>)
  tokens: number // gutgeschriebene Tokens
  tokens_remaining: number // davon noch nicht verbraucht
  notes: string | null
}

export interface BountyHistoryResponse {
  count: number
  total_tokens: number
  entries: BountyLedgerEntry[]
}

/// Per-tenant credential readiness. See Cortex `McpServerInfo` docs.
///  - 'ready'       → tenant_credentials has rows; Aria can use it
///  - 'missing'     → service granted, but no credentials configured
///  - 'not_granted' → service not licensed for this tenant
///  - 'shared'      → no per-tenant creds needed (infra sidecars)
export type CortexCredentialsStatus = 'ready' | 'missing' | 'not_granted' | 'shared'

export interface CortexMcpServer {
  name: string
  connected: boolean
  tool_count: number
  credentials_status?: CortexCredentialsStatus
}

export interface CortexMcpStatus {
  servers: CortexMcpServer[]
  total_tools: number
}

/// Masked credential entry returned by GET /admin/tenants/<tenant>/credentials.
/// `secret_preview` shows the last 4 chars; full value never leaves the server.
export interface CortexCredentialEntry {
  service_type: string
  service_instance: string
  credential_kind: string
  username: string | null
  secret_preview: string
  last_used_at: string | null
  updated_at: string
  updated_by: string | null
}

export interface CortexCredentialsListResponse {
  tenant: string
  services: Record<string, CortexCredentialEntry[]>
}

/// Single field in a PUT body — only fields with a non-empty secret are
/// upserted; missing fields preserve existing rows server-side.
export interface CortexCredentialUpsert {
  secret: string
  username?: string
  expires_at?: string
}

/// User-level (personal-delegate) credentials. Per Cortex `dynamic.rs:495`
/// these win over tenant-level credentials when both exist — used for
/// "Aria handelt im Auftrag eines konkreten Users" (Modell 3 Hybrid).
export interface CortexUserSidecarSummary {
  sidecar_name: string
  keys: string[]
}

export interface CortexUserSidecarKey {
  key: string
  value_preview: string
}

export interface CortexUserSidecarKeyInfo {
  sidecar_name: string
  keys: CortexUserSidecarKey[]
}

export interface CortexMeSettings {
  ai_mode: string
  preferred_model: string
  max_tokens_per_day: number | null
  billing_visible: boolean
  tenant: string
  overrides: {
    ai_mode: string | null
    preferred_model: string | null
    max_tokens_per_day: number | null
  }
}

// TQ #6962-T4 (SPEC-Personal-Devices-Tab): Personal-Mode Geräte des
// eingeloggten Users (GET /api/me/devices). Schema-kompatibel zur Org-Variante
// (handlers.GetMyDevices liefert dieselbe `orgDevice`-Struct wie GetOrgDevices).
// Scope: rein owner_user_id (JWT-Sub) + org_slug IS NULL → nur persönliche
// Geräte, keine Org-Devices bei denen der User zufällig Owner ist.
export interface MyDevice {
  device_id: string
  hostname: string
  device_type: string
  agent_version: string
  enrolled_at: string
  enrolled_via?: string
  last_seen_at: string
  status: string
  tags: string[]
  description: string
  capabilities?: Record<string, unknown>
  owner_user_id?: string | null
}

export interface MyDevicesResponse {
  devices: MyDevice[]
  count: number
}

// TQ #7128: Staff Knowledge-Tab. Backend GET /api/staff/knowledge (#7114)
// liest cortex.knowledge_docs (enabled=true) und liefert {documents, total}.
export interface StaffKnowledgeDoc {
  id?: string
  title: string
  source_path: string
  chunk_count: number
  updated_at: string
  tenant_id: string
  scope: string[]
}

export interface StaffKnowledgeResponse {
  documents: StaffKnowledgeDoc[]
  total: number
}

// TQ #9696 (Staff-Migration Teil C, Bundle #9523): Service-Requests,
// portiert aus customer-portal/src/services/api.ts (staffListPendingRequests
// etc. → admin-api /admin/service-requests, staff-gated).
export interface StaffServiceRequest {
  id: string
  customer_email: string
  service_slug: string
  service_name: string
  status: 'pending' | 'in_progress' | 'completed' | 'rejected' | 'cancelled'
  assigned_to?: string
  admin_notes?: string
  created_at: string
  updated_at: string
  payload?: Record<string, unknown>
}

// TQ #9698 (Staff-Migration Teil E, portiert von customer-portal
// pages/staff/StaffCortex.tsx + StaffCortexPermissions.tsx): Cortex-Nutzung
// + Standing-Permissions. Backend: portal-api /api/staff/cortex/* (Proxy zu
// Cortex admin-API via CORTEX_ADMIN_TOKEN service-token + requireStaff()
// Gate) — siehe services/portal-api/internal/handlers/staff_proxy.go.
export interface StaffCortexPermission {
  id: string
  user_id: string
  permission_type: 'allow' | 'deny' | 'confirm'
  scope: 'tool' | 'server' | 'category'
  target: string
  constraints: unknown | null
  granted_at: string
  expires_at: string | null
  granted_via: string
}

export interface StaffCortexTenantUsage {
  tenant: string
  tokens_in: number
  tokens_out: number
  cost_eur: number
  events: number
  last_event_at?: string
}

export interface StaffCortexUsage {
  tenants: StaffCortexTenantUsage[]
  total_tokens_in: number
  total_tokens_out: number
  total_cost_eur: number
  total_events: number
  total_topup_remaining: number
  window_days: number
}

// TQ #10589 (SPEC-Cockpit-Verbrauchsanzeige.md §5, Zielbild PROJ-Cortex-Billing):
// Cockpit-Verbrauchsanzeige — Token-Pool + Kosten je Nutzungsart. Backend:
// portal-api /api/{org/:slug,me,staff}/usage (Aggregation aus Cortex
// usage/breakdown + tq-service tenant-usage-Views) — siehe org_usage.go.
export interface UsageCategory {
  units: number
  eur: number
  event_count?: number
}

// Token-Kontingent Typ 2 (IT-Service-Kontingent) — GET /api/org/:slug/erpnext/quota.
// _mio-Felder sind bereits IT-SCE-Token in Millionen, kein Sonnet-Rohtoken-Budget.
export interface TokenQuota {
  name: string // ERPNext Token-Kontingent Doc-Name, z.B. "TK-0004"
  verbraucht_mio: number
  verbleibend_mio: number
  gesamt_mio: number
  status: string
  unit: 'it_sce_token_mio'
}

// TQ #380: Drilldown-Buchungszeilen zum Token-Kontingent —
// GET /api/org/:slug/erpnext/quota/bookings. Kein Euro-/Kursfeld im Kundenpfad.
export interface TokenQuotaBooking {
  beleg: string // Token-Buchung-Belegnummer
  datum: string
  typ: string // COGS | Infra | Labor
  einheiten_mio: number
  // TQ #960: wird bewusst nicht angezeigt (Regiebericht kennt keine Kategorie, Patric 25.10. 16:55)
  kategorie?: string
  taetigkeit?: string
}

export interface TokenQuotaBookings {
  kontingent: string
  stichtag: string
  bookings: TokenQuotaBooking[]
  summe_einheiten_mio: number
  unit: 'it_sce_token_mio'
}

export interface TaskTokens {
  month: string
  tasks_done: number
  tokens_input: number
  tokens_output: number
  tokens_total: number
  /** true = nur erledigte Auftraege zaehlen */
  only_done?: boolean
  /** false = Zahlen nicht ermittelbar (TQ-Fehler / Monat zu alt), Zahlenfelder dann 0; fehlt = true */
  available?: boolean
}

export interface UsageResponse {
  tenant: string
  month: string
  // cockpit-api (Kunden-Instanz): nicht ermittelbare Kategorien sind null (nie 0).
  categories: {
    aria_chat?: UsageCategory | null
    task_work?: UsageCategory | null
    human_events?: UsageCategory | null
  }
  // Nur cockpit-api: Summen sind null, wenn nicht vollstaendig ermittelbar.
  total_units?: number | null
  total_eur?: number | null
  partial?: boolean
  unavailable?: string[]
  unavailable_reasons?: Record<string, string>
  // Optional (nur Org-Pfad, neuere Backends): Token-Zahlen der KI-Auftraege des Monats.
  // Eigene Groesse, NICHT mit categories (units/eur) vermischen.
  task_tokens?: TaskTokens
  // NUR im Staff-Detail (/staff/usage/:tenant_slug) gesetzt — SPEC §4b/§7 Q3:
  // Marge ist Staff-only, nie kundenfakturiert.
  internal_cost_eur?: number
  margin_pct?: number | null
  consistency_warnings?: string[]
}

export interface StaffUsageOverview {
  month: string
  tenants: UsageResponse[]
}

// TQ #9697 (Staff-Migration Teil D, Bundle #9523): Plattform-Health
// (System-Health, VMs, ERP-Sync), portiert aus customer-portal/src/services/
// api.ts.
export interface StaffVM {
  id: number
  vm_id: number
  vm_name: string
  hostname?: string
  ip_address?: string
  status: string
  cpu_cores?: number
  memory_mb?: number
  disk_gb?: number
  operating_system?: string
  monthly_price?: number
  cost_rate?: number
  /** external = normaler Kunde (Stripe), internal = Mitarbeiter
   *  (Verrechnungskonto), reseller = Mitarbeiter verkauft an Endkunde weiter. */
  billing_mode?: 'external' | 'internal' | 'reseller'
  customer_id?: number
  customer_email?: string
  customer_name?: string
  organization_id?: number
  organization_name?: string
  proxmox_status?: string
  cpu_usage?: number
  memory_usage?: number
  uptime_seconds?: number
  last_proxmox_sync?: string
}

export interface StaffVMStats {
  total: number
  running: number
  stopped: number
  suspended?: number
  assigned?: number
  unassigned: number
  // The backend doesn't aggregate cpu/ram totals — left optional in case
  // a future repo change adds them.
  total_cpu_cores?: number
  total_memory_gb?: number
  total_disk_gb?: number
}

// Backend shape: {counts: {total/healthy/degraded/unhealthy/unknown},
// services[], overall, offline, message, timestamp}
export interface StaffHealthSummary {
  counts: {
    total: number
    healthy: number
    degraded: number
    unhealthy: number
    unknown: number
  }
  overall: 'healthy' | 'degraded' | 'unhealthy' | 'unknown' | string
  offline?: boolean
  message?: string
  timestamp?: string
  services?: { name: string; status: string }[]
}

// Backend shape: {services: [{name, displayName, status, lastCheck,
// responseTimeMs, uptime24h, uptime7d}], offline, overallStatus, message}
export interface StaffHealthService {
  name: string
  displayName?: string
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown' | string
  lastCheck?: string
  responseTimeMs?: number
  uptime24h?: number
  uptime7d?: number
}

export interface StaffHealthServicesResponse {
  services: StaffHealthService[]
  offline?: boolean
  overallStatus?: string
  message?: string
}

export interface StaffHealthIncident {
  id: string
  service: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  status: 'open' | 'investigating' | 'resolved'
  started_at: string
  resolved_at?: string
  description?: string
}

export interface SyncEntry {
  id: number
  entity_type: string
  entity_id: number
  entity_name: string | null
  status: 'success' | 'failed' | 'pending'
  error_message: string | null
  created_at: string
  updated_at: string
}

// TQ #9695 (Staff-Migration Teil B, portiert von customer-portal
// pages/staff/StaffInvoices.tsx + StaffSubscriptions.tsx): admin-api-Typen
// fuer Rechnungen + Service-Instanzen. Lokal statt geteiltem Package, da die
// admin-api-Shapes stabil genug sind (gleiche Begruendung wie im
// customer-portal-Vorbild).
export interface StaffInstance {
  id: number
  customer_id: number
  customer_email?: string
  customer_name?: string
  service_id: number
  service_name?: string
  service_slug?: string
  instance_name: string
  display_name?: string
  status: string
  subscription_status?: string
  payment_status?: string
  next_billing_date?: string
  billing_cycle?: string
  domain?: string
  created_at: string
}

export interface StaffInvoice {
  id: string
  customer: string
  customer_name?: string
  posting_date: string
  due_date: string
  grand_total: number
  outstanding_amount: number
  status: 'Paid' | 'Unpaid' | 'Overdue' | 'Cancelled' | string
  currency?: string
}

export interface StaffInvoiceStats {
  total_count: number
  total_outstanding: number
  total_paid: number
  total_overdue: number
  this_month_count: number
  this_month_total: number
}

// TQ #9694 (Staff-Migration Teil A, Bundle #9523): Kunden + Organisationen,
// portiert aus customer-portal/src/services/api.ts (staffListCustomers etc.).
export interface StaffCustomer {
  id: string
  username: string
  email: string
  firstName?: string
  lastName?: string
  enabled: boolean
  emailVerified: boolean
  createdAt: string
  lastLogin?: string
  activeServices?: number
  monthlySpend?: number
  // ERPNext is the single source of truth for billing — `erpnextCustomerId`
  // is the link key used by subscriptions_cache and ERPNext invoices.
  erpnextCustomerId?: string | null
  portalCustomerId?: number | null
  /** is_employee=true → Mitarbeiter-Hosting (Selbstkosten, Verrechnungskonto) */
  isEmployee?: boolean
  erpnextEmployeeId?: string | null
  attributes?: Record<string, unknown>
}

export interface StaffOrganization {
  id: string
  name: string
  slug: string
  email?: string
  phone?: string
  vat_id?: string
  description?: string
  status: string
  keycloak_realm_name?: string
  realm_status?: string
  member_count?: number
  created_at: string
  updated_at: string
}

// TQ #9699 (Staff-Migration Teil F): Lizenzen + Downloads-Releases.
// Backend-Proxies bereits live (portal-api staff_proxy.go, seit customer-portal-Aera):
// GET /api/staff/licenses -> license-server admin API
// GET /api/staff/downloads/products + /api/staff/downloads/stats -> downloads-service admin API
export interface StaffLicense {
  id: string
  key: string
  customer_id?: string
  customer_email?: string
  product_id?: string
  product_slug?: string
  max_seats?: number
  max_instances?: number
  starts_at?: string
  expires_at?: string
  license_type?: string
  revoked_at?: string
  revoke_reason?: string
  is_internal?: boolean
  created_at: string
}

export interface StaffDownloadProduct {
  id: number
  slug: string
  name: string
  description?: string
  product_type?: string
  is_public?: boolean
  total_releases?: number
  total_downloads?: number
}

export interface StaffDownloadStats {
  total_products: number
  total_releases: number
  total_downloads: number
  total_storage_bytes?: number
  recent_downloads_24h?: number
}

// TQ #9701: Verdienst-Konten, Auszahlungen, Aria Multi-Tenant-Registry (Staff-Migration
// Teil G, aus customer-portal StaffEarnings.tsx/StaffAriaTenants.tsx portiert).
export interface StaffEmployeeSaldo {
  employee_id: string
  employee_name: string
  balance: number
  last_entry_date?: string
  this_month_in: number
  this_month_out: number
  vereinbarte_auszahlung: number
  max_auszahlung: number
}

export interface StaffPayoutProposal {
  employee_id: string
  employee_name: string
  balance: number
  vereinbarte_auszahlung: number
  max_auszahlung: number
  proposed_amount: number
  reason?: string
  balance_after: number
}

export interface StaffPayoutExecuteResult {
  employee_id: string
  success: boolean
  error?: string
}

export interface StaffAriaTenant {
  tenant: string
  realm: string
  status: 'active' | 'disabled'
  note?: string
  client_secret_redacted: string
  created_at: string
  created_by_user_email?: string
  updated_at: string
}

export interface StaffAriaTenantCreateRequest {
  tenant: string
  realm: string
  client_secret: string
  note?: string
}

class ApiService {
  private client: AxiosInstance
  private token: string = ''
  private cortexBaseURL: string

  constructor() {
    this.cortexBaseURL = '' // main.tsx setzt ihn nach dem Config-Load
    this.client = axios.create({
      baseURL: apiOrigin(),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add auth header to all requests
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`
      }
      return config
    })
  }

  setToken(token: string) {
    this.token = token
  }

  /** TQ #409: API-Basis aus der Laufzeit-Config (vor dem ersten Request). */
  setBaseURL(url: string) {
    this.client.defaults.baseURL = url
  }

  // Glasbrenner-Cockpit-Fix (23.09.2026): cortexBaseURL startet leer; main.tsx
  // setzt ihn nach dem Config-Load einmal (cortexUrl aus der Config, sonst per
  // CortexHostAPI.resolve im geteilten Cockpit). Alle cortexRequest()-Aufrufe
  // und der WebSocket-Stream lesen this.cortexBaseURL live, kein
  // Re-Konstruieren noetig.
  setCortexBaseURL(url: string) {
    this.cortexBaseURL = url
  }

  getCortexBaseURL(): string {
    return this.cortexBaseURL
  }

  // TQ #6906: Scout public API (kein Auth) — fuer Scout-Landing-Page auf
  // with.it-sce.de Pre-Login. Public endpoints werden vom Backend mit
  // service-level Cortex token + IP-based rate-limiting versehen.
  async publicScoutChat(
    message: string,
    sessionId?: string,
    // TQ #8277: proaktiver Onboarding-Opener. Wenn opener=true gesetzt ist,
    // liefert der Server eine proaktive Eroeffnungs-Assistant-Message (Text
    // kommt aus der Soul, NICHT hardcoded im Frontend).
    extra?: { opener?: boolean; hint?: string; onboarding_stage?: string },
  ): Promise<CortexChatResponse> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 45_000)
    try {
      const response = await this.client.post(
        '/api/public/scout-chat',
        { message, session_id: sessionId, ...(extra ?? {}) },
        { signal: controller.signal as any, headers: { Authorization: '' } },
      )
      return {
        session_id: response.data.session_id,
        response: response.data.response,
      } as CortexChatResponse
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        throw { error: 'timeout: Scout antwortet nicht (45s)' }
      }
      throw err
    } finally {
      clearTimeout(timeout)
    }
  }

  async publicScoutHistory(
    sessionId: string,
  ): Promise<{ role: 'user' | 'assistant'; content: string }[]> {
    const response = await this.client.get(
      `/api/public/scout-history?session_id=${encodeURIComponent(sessionId)}`,
      { headers: { Authorization: '' } },
    )
    return response.data.messages ?? []
  }

  // TQ #8277/#8278: Onboarding-State fuer proaktiven Scout-Auto-Open.
  // Authentifizierte Endpoints (/api/me/...) — Auth-Header kommt vom Interceptor.
  async getOnboardingState(): Promise<OnboardingState> {
    const response = await this.client.get('/api/me/onboarding-state')
    return response.data as OnboardingState
  }

  /** Markiert das Onboarding als "nicht mehr anzeigen" (Dismiss-Checkbox). */
  async ackScoutOnboarding(): Promise<void> {
    await this.client.post('/api/me/scout-onboarding/ack', {})
  }

  /** Telemetrie: Auto-Open wurde gezeigt bzw. erster echter User-Reply kam. */
  async scoutOnboardingAutoOpen(firstReply?: boolean): Promise<void> {
    await this.client.post(
      '/api/me/scout-onboarding/auto-open',
      firstReply ? { first_reply: true } : {},
    )
  }

  // Organizations
  async getMyOrganizations() {
    const response = await this.client.get('/api/organizations/my')
    return response.data
  }

  // TQ #7975: Tenant-Switcher — Personal + alle Org-Memberships des Users.
  async getMyTenants(): Promise<MyTenantsResponse> {
    const response = await this.client.get('/api/me/tenants')
    return response.data
  }

  // TQ #6962-T4 (SPEC-Personal-Devices-Tab): Geräte des eingeloggten Users im
  // Personal-Mode (with.it-sce.de). DSGVO: Pfad trägt KEINEN Identifier — das
  // Backend filtert ausschließlich über den verifizierten JWT-Sub (owner_user_id).
  async getMyDevices(statusFilter?: 'all' | 'online' | 'offline'): Promise<MyDevicesResponse> {
    const qs = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : ''
    const response = await this.client.get(`/api/me/devices${qs}`)
    const data = response.data ?? {}
    return {
      devices: Array.isArray(data.devices) ? data.devices : [],
      count: typeof data.count === 'number' ? data.count : (data.devices?.length ?? 0),
    }
  }

  // TQ #7128: Staff Knowledge — listet cortex.knowledge_docs (staff-auth).
  // Reindex-Pfad (cortex-Endpoint) noch offen → Tab ist read-only (MVP).
  async getStaffKnowledge(): Promise<StaffKnowledgeResponse> {
    const response = await this.client.get('/api/staff/knowledge')
    const data = response.data ?? {}
    return {
      documents: Array.isArray(data.documents) ? data.documents : [],
      total: typeof data.total === 'number' ? data.total : (data.documents?.length ?? 0),
    }
  }

  // ----- Cortex usage (portal-api -> cortex-db, TQ #9698, Teil E) ----------
  async staffGetCortexUsage(): Promise<StaffCortexUsage> {
    const response = await this.client.get('/api/staff/cortex/usage')
    return response.data
  }

  // ----- Cockpit-Verbrauchsanzeige (TQ #10589, SPEC-Cockpit-Verbrauchsanzeige.md §5) -----
  async getOrgUsage(slug: string, month?: string): Promise<UsageResponse> {
    const response = await this.client.get(`/api/org/${slug}/usage`, { params: month ? { month } : undefined })
    return response.data
  }

  async getMyUsage(month?: string): Promise<UsageResponse> {
    const response = await this.client.get('/api/me/usage', { params: month ? { month } : undefined })
    return response.data
  }

  async staffGetUsage(month?: string): Promise<StaffUsageOverview> {
    const response = await this.client.get('/api/staff/usage', { params: month ? { month } : undefined })
    return response.data
  }

  async staffGetUsageDetail(tenantSlug: string, month?: string): Promise<UsageResponse> {
    const response = await this.client.get(`/api/staff/usage/${tenantSlug}`, { params: month ? { month } : undefined })
    return response.data
  }

  // ----- Cortex permissions (portal-api -> cortex-db, TQ #9698, Teil E) -----
  async staffListCortexPermissions(): Promise<StaffCortexPermission[]> {
    const response = await this.client.get('/api/staff/cortex/permissions')
    return response.data
  }
  async staffCreateCortexPermission(
    userId: string,
    body: { permission_type: string; scope: string; target: string; expires_at?: string },
  ): Promise<{ id: string }> {
    const response = await this.client.post(`/api/staff/cortex/users/${userId}/permissions`, body)
    return response.data
  }
  async staffDeleteCortexPermission(userId: string, permId: string): Promise<{ deleted: boolean }> {
    const response = await this.client.delete(`/api/staff/cortex/users/${userId}/permissions/${permId}`)
    return response.data
  }

  // ==========================================================================
  // Staff API — Kunden/Organisationen (TQ #9694, Bundle #9523 Teil A) +
  // Rechnungen/Service-Instanzen (TQ #9695, Teil B) +
  // Plattform-Health/VMs/Sync (TQ #9697, Teil D).
  //
  // Diese Endpoints leben auf admin-api (`/admin/*`, staff-gated), NICHT auf
  // portal-api `/api/staff/*` (dort existieren nur knowledge/earnings/licenses
  // etc. — /admin/customers und /admin/organizations wurden dort nie
  // hinzugefügt). admin-api ist über dieselbe öffentliche Domain
  // (api.it-sce.de) erreichbar wie portal-api, nur unter dem `/admin`-statt
  // `/api`-Pfad — daher ein eigener staffRequest-Helper mit absoluter Base-URL
  // statt `this.client` (dessen baseURL relativ ist und über die nginx-`/api/`-
  // Proxy-Regel läuft, die `/admin` nicht kennt). Live verifiziert 2026-07-07:
  // https://api.it-sce.de/admin/customers → 401 (erreichbar, nur Auth fehlt),
  // CORS_ALLOWED_ORIGINS auf admin-api enthält bereits https://cockpit.it-sce.de
  // (org-portals Staff-Cockpit-Origin). Mirrors customer-portal/src/services/
  // api.ts staffRequest 1:1.
  // ==========================================================================
  private get staffApiBase(): string {
    return getRuntimeConfig().adminApiBase ?? ''
  }

  private async staffRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.staffApiBase}${path}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    const resp = await fetch(url, { ...options, headers })
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({
        error: `HTTP ${resp.status}: ${resp.statusText}`,
      }))
      throw errorData
    }
    return resp.json()
  }

  // ----- Customers (Keycloak Personal-Users + ERPNext) ---------------------

  async staffListCustomers() {
    return this.staffRequest<{ success: boolean; customers: StaffCustomer[] }>(
      '/admin/customers',
    )
  }
  async staffEnableCustomer(id: string) {
    return this.staffRequest(`/admin/customers/${id}/enable`, { method: 'POST' })
  }
  async staffDisableCustomer(id: string) {
    return this.staffRequest(`/admin/customers/${id}/disable`, { method: 'POST' })
  }
  async staffResetCustomerPassword(id: string) {
    return this.staffRequest(`/admin/customers/${id}/reset-password`, { method: 'POST' })
  }

  // ----- Organizations (alle Workspace-Orgs quer ueber Realms) -------------

  async staffListOrganizations() {
    return this.staffRequest<{ organizations: StaffOrganization[] }>(
      '/admin/organizations',
    )
  }
  async staffApproveOrganization(id: string) {
    return this.staffRequest(`/admin/organizations/${id}/approve`, { method: 'POST' })
  }
  async staffRejectOrganization(id: string, reason: string) {
    return this.staffRequest(`/admin/organizations/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  // ----- Geschäft: Rechnungen + Service-Instanzen (TQ #9695, Teil B) --------

  async staffListInvoices(params?: { status?: string; limit?: number }) {
    const qs = params
      ? '?' + new URLSearchParams(params as Record<string, string>).toString()
      : ''
    return this.staffRequest<{ invoices: StaffInvoice[]; total: number }>(
      `/admin/invoices${qs}`,
    )
  }
  async staffGetInvoiceStats() {
    return this.staffRequest<StaffInvoiceStats>('/admin/invoices/stats')
  }
  async staffListOverdueInvoices() {
    return this.staffRequest<{ invoices: StaffInvoice[] }>('/admin/invoices/overdue')
  }
  async staffListInstances() {
    return this.staffRequest<{ instances: StaffInstance[] }>('/admin/instances')
  }

  // ----- System Health (TQ #9697, Teil D) ----------------------------------

  async staffGetHealthSummary() {
    return this.staffRequest<StaffHealthSummary>('/admin/health/summary')
  }
  async staffListHealthServices() {
    return this.staffRequest<StaffHealthServicesResponse>('/admin/health/services')
  }
  async staffListHealthIncidents() {
    return this.staffRequest<{ incidents: StaffHealthIncident[] }>(
      '/admin/health/incidents',
    )
  }

  // ----- VMs (TQ #9697, Teil D) --------------------------------------------

  async staffListVMs(params?: { customer_id?: number; organization_id?: number }) {
    const qs = new URLSearchParams()
    if (params?.customer_id) qs.set('customer_id', String(params.customer_id))
    if (params?.organization_id) qs.set('organization_id', String(params.organization_id))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    // Backend: {success, vms: StaffVM[]|null, pagination, stats}
    return this.staffRequest<{
      vms: StaffVM[] | null
      pagination?: { total: number; page: number; limit: number; total_pages: number }
      stats?: StaffVMStats
    }>(`/admin/vms${suffix}`)
  }
  async staffGetVMStats() {
    // Backend wraps in {stats: {}, success: true}
    return this.staffRequest<{ stats: StaffVMStats; success: boolean }>('/admin/vms/stats')
  }
  async staffListUnassignedVMs() {
    return this.staffRequest<{ vms: StaffVM[] | null }>('/admin/vms/unassigned')
  }
  async staffAssignVM(vmId: number, data: { type: 'customer' | 'organization'; target_id: number; monthly_price?: number; notes?: string }) {
    return this.staffRequest(`/admin/vms/${vmId}/assign`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }
  async staffUnassignVM(vmId: number) {
    return this.staffRequest(`/admin/vms/${vmId}/unassign`, { method: 'POST' })
  }

  // ----- Sync (erpnext-sync monitoring, TQ #9697, Teil D) ------------------

  async staffGetSyncStats() {
    return this.staffRequest<{ total: number; successful: number; failed: number; pending: number; not_synced: number }>('/admin/sync/statistics')
  }
  async staffGetSyncStatus() {
    return this.staffRequest<{ status: string; services: { database: string; erpnext: string }; version?: string; timestamp?: string }>('/admin/sync/status')
  }
  async staffGetSyncRecent(limit = 30) {
    return this.staffRequest<{ syncs: SyncEntry[] }>(`/admin/sync/recent?limit=${limit}`)
  }
  async staffTriggerSyncBatch() {
    return this.staffRequest('/admin/sync/batch', { method: 'POST' })
  }

  // TQ #9699: Lizenzen + Downloads-Releases (Staff-Migration Teil F, aus
  // customer-portal StaffLicenses.tsx/StaffDownloads.tsx portiert).
  private static unwrapArray<T>(payload: unknown): T[] {
    if (!payload) return []
    if (Array.isArray(payload)) return payload as T[]
    const data = (payload as { data?: T[] }).data
    return Array.isArray(data) ? data : []
  }

  async getStaffLicenses(): Promise<StaffLicense[]> {
    const response = await this.client.get('/api/staff/licenses')
    return ApiService.unwrapArray<StaffLicense>(response.data)
  }

  async getStaffDownloadProducts(): Promise<StaffDownloadProduct[]> {
    const response = await this.client.get('/api/staff/downloads/products')
    return ApiService.unwrapArray<StaffDownloadProduct>(response.data)
  }

  async getStaffDownloadStats(): Promise<StaffDownloadStats | null> {
    try {
      const response = await this.client.get('/api/staff/downloads/stats')
      return response.data ?? null
    } catch {
      return null
    }
  }

  // TQ #9701: Verdienst-Konten (Salden), Auszahlungen (Staff-Migration Teil G).
  async getStaffEarnings(): Promise<StaffEmployeeSaldo[]> {
    const response = await this.client.get('/api/staff/earnings')
    return ApiService.unwrapArray<StaffEmployeeSaldo>(response.data?.employees ?? response.data)
  }

  async getStaffPayoutProposals(): Promise<{ proposals: StaffPayoutProposal[]; confirm_token: string }> {
    const response = await this.client.get('/api/staff/payout/proposals')
    const data = response.data ?? {}
    return {
      proposals: ApiService.unwrapArray<StaffPayoutProposal>(data.proposals),
      confirm_token: data.confirm_token ?? '',
    }
  }

  async postStaffPayoutExecute(
    confirmToken: string,
    items: { employee_id: string; amount: number; note?: string }[],
  ): Promise<{ results: StaffPayoutExecuteResult[] }> {
    const response = await this.client.post('/api/staff/payout/execute', { confirm_token: confirmToken, items })
    return { results: ApiService.unwrapArray<StaffPayoutExecuteResult>(response.data?.results) }
  }

  // TQ #9701: Registrierungs-Tokens (admin-api, gleicher Host wie der uebrige
  // ApiService-Client — kein separater Axios-Client noetig).
  async getStaffTokens(): Promise<TToken[]> {
    const response = await this.client.get('/api/admin/tokens')
    return ApiService.unwrapArray<TToken>(response.data?.data ?? response.data)
  }

  async postStaffToken(req: TTokenCreateRequest): Promise<TToken> {
    const response = await this.client.post('/api/admin/tokens', req)
    return response.data?.data ?? response.data
  }

  async deleteStaffToken(id: number): Promise<void> {
    await this.client.delete(`/api/admin/tokens/${id}`)
  }

  // TQ #9701: Aria Multi-Tenant-Registry (Staff-Migration Teil G).
  async getStaffAriaTenants(): Promise<StaffAriaTenant[]> {
    const response = await this.client.get('/api/staff/aria/tenants')
    return ApiService.unwrapArray<StaffAriaTenant>(response.data?.tenants ?? response.data)
  }

  async postStaffAriaTenant(req: StaffAriaTenantCreateRequest): Promise<StaffAriaTenant> {
    const response = await this.client.post('/api/staff/aria/tenants', req)
    return response.data
  }

  async patchStaffAriaTenantStatus(tenant: string, status: 'active' | 'disabled'): Promise<void> {
    await this.client.patch(`/api/staff/aria/tenants/${encodeURIComponent(tenant)}/status`, { status })
  }

  async deleteStaffAriaTenant(tenant: string): Promise<void> {
    await this.client.delete(`/api/staff/aria/tenants/${encodeURIComponent(tenant)}`)
  }

  async getOrganization(slug: string) {
    const response = await this.client.get(`/api/org/${slug}`)
    return response.data
  }

  async updateOrganization(slug: string, data: {
    name?: string
    legal_name?: string
    description?: string
    contact_email?: string
    contact_phone?: string
  }) {
    const response = await this.client.put(`/api/org/${slug}`, data)
    return response.data
  }

  /** TQ #5707-a / #5710: Setzt onboarding_status fuer aktuellen Member.
      Backend validiert status ∈ {pending, completed, skipped}. */
  async setMyOnboardingStatus(slugOrToken: string, status: 'pending' | 'completed' | 'skipped') {
    const response = await this.client.put(`/api/org/${slugOrToken}/me/onboarding-status`, { status })
    return response.data
  }

  // Dashboard
  async getDashboard(slug: string) {
    const response = await this.client.get(`/api/org/${slug}/dashboard`)
    return response.data
  }

  // Token-Kontingent Typ 2 (IT-Service-Kontingent, ERPNext Token-Kontingent/
  // Token-Buchung, TK-####) — read-only live aus ERPNext, siehe
  // ADR-Cockpit-Live-Daten-und-Token-Einheit.md. Liefert bereits IT-SCE-Token
  // (in Mio, _mio-Felder), keine Umrechnung im Frontend. Getrennt von Typ 1
  // (Cortex-Aria-Workspace-Quota, getOrgAriaQuota/token-budget oben).
  async getOrgTokenQuota(slug: string): Promise<TokenQuota> {
    const response = await this.client.get(`/api/org/${slug}/erpnext/quota`)
    return response.data
  }

  async getOrgTokenQuotaBookings(slug: string): Promise<TokenQuotaBookings> {
    const response = await this.client.get(`/api/org/${slug}/erpnext/quota/bookings`)
    return response.data
  }

  // TQ #6933: Personal-Mode-Dashboard (with.it-sce.de). Schema-kompatibel zu
  // /api/org/:slug/dashboard (member_count=1, pending_invites=0), LIVE seit 485fd6e.
  async getMeDashboard() {
    const response = await this.client.get('/api/me/dashboard')
    return response.data
  }

  // TQ #7072: Welcome-Modal (Personal-User Onboarding). Backend-Endpoints unter
  // /api/me (services/portal-api/internal/handlers/welcome.go).
  // GET /api/me/welcome-pending → aktive gebuchte Services ohne welcome_acks-Zeile.
  async getWelcomePending(): Promise<{
    success: boolean
    cards: { service_type: string; name: string; variant_name?: string; item_group?: string }[]
    count: number
  }> {
    const response = await this.client.get('/api/me/welcome-pending')
    return response.data
  }

  // POST /api/me/welcome/ack → markiert die übergebenen service_types als gesehen.
  // Idempotent (UPSERT welcome_acks). Danach kommen sie nicht mehr in welcome-pending.
  async ackWelcome(serviceTypes: string[]): Promise<{ success: boolean; acked: number }> {
    const response = await this.client.post('/api/me/welcome/ack', { service_types: serviceTypes })
    return response.data
  }

  // TQ #6963: Per-User KI-Persona (Personal-Mode), Server-Sync statt localStorage.
  async getMyPersona(): Promise<{ success: boolean; persona: string; updated_at?: string }> {
    const response = await this.client.get('/api/me/persona')
    return response.data
  }

  async updateMyPersona(persona: string): Promise<{ success: boolean }> {
    const response = await this.client.put('/api/me/persona', { persona })
    return response.data
  }

  // TQ #7976: Per-User Soul-Addendum (Personal-Mode Aria-Charakter).
  async getMyUserSoul(): Promise<{ success: boolean; soul_addendum: string }> {
    const response = await this.client.get('/api/me/soul')
    return response.data
  }

  async putMyUserSoul(soul_addendum: string): Promise<{ success: boolean }> {
    const response = await this.client.put('/api/me/soul', { soul_addendum })
    return response.data
  }

  // TQ #7646: Per-User-Rechnungsadresse (Personal-Mode). Body wird 1:1 in die
  // ERPNext-Billing-Address gespiegelt (Feldnamen = ERPNext: pincode = PLZ).
  // Auth + same-origin laufen ueber den geteilten axios-Client (Bearer-JWT).
  async updateMyAddress(data: {
    address_line1: string
    address_line2: string
    city: string
    pincode: string
  }): Promise<{ success: boolean }> {
    const response = await this.client.put('/api/me/address', data)
    return response.data
  }

  // TQ #10084: Self-Service-Konto-Löschung. Backend blockt Org-Owner mit 409
  // (success:false, blocked:true, organization/other_members/active_services
  // im Body) statt zu löschen — der Aufrufer muss diesen Fall separat von
  // "Passwort falsch" (401) und "sonstiger Fehler" behandeln, s. AxiosError.
  async deleteMyAccount(password: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete('/api/me/account', {
      data: { confirm: true, password },
    })
    return response.data
  }

  // Aria-Token-Quota (TQ #5763) — BASE-Limit + Top-Up + Monatsverbrauch +
  // Welcome-Bonus-Hint. Wird vom Cockpit-Aria-Tab gerendert.
  async getOrgAriaQuota(slug: string): Promise<AriaQuotaResponse> {
    const response = await this.client.get(`/api/org/${slug}/aria/quota`)
    return response.data as AriaQuotaResponse
  }

  // TQ #8033: Per-User-Variante für Personal-Mode (with.it-sce.de) — kein
  // OrgContext, proxied Cortex /api/v1/me/token-budget. Gleiche Response-Form.
  async getMyAriaQuota(): Promise<AriaQuotaResponse> {
    const response = await this.client.get(`/api/me/aria/quota`)
    return response.data as AriaQuotaResponse
  }

  // TQ #8684: Token-Bounty-Ledger des eingeloggten Users (Cockpit-Transparenz,
  // ZIELBILD §8.6). Proxied Cortex /api/v1/me/bounty-history via portal-api.
  async getMyBountyHistory(): Promise<BountyHistoryResponse> {
    const response = await this.client.get(`/api/me/bounty-history`)
    return response.data as BountyHistoryResponse
  }

  // Top-Up Stripe-Checkout (existing endpoint stripe_topup_checkout.go).
  // Erzeugt eine Stripe-Checkout-Session fuer ein TOPUP-Paket.
  // TQ #8130/#7063: org-portal Top-Ups sind per-User (AriaQuotaWidget zeigt die
  // Quota des eingeloggten Users). scope='user' signalisiert dem Backend, die
  // Buyer-Identitaet als user_sub in die Stripe-Metadata zu stempeln (aus dem
  // JWT, serverseitig) → Webhook bucht auf den persoenlichen Pool des Users.
  async createTopUpCheckout(packageId: string): Promise<{ success: boolean; checkout_url: string; session_id: string }> {
    const response = await this.client.post(`/api/checkout/topup`, { package_id: packageId, scope: 'user' })
    return response.data
  }

  // TQ #8686: Token-Bounty Claim-Code einlösen (Conversion-Hook). Proxied an
  // Cortex POST /api/v1/me/bounty/claim (#8672) via portal-api. Erfolg →
  // {claimed,tokens,severity}; Fehler (Flag aus / ungültig / bereits eingelöst)
  // → 400 mit Fehlertext (axios wirft, Caller fängt + zeigt die Meldung).
  async claimBounty(claimCode: string): Promise<BountyClaimResult> {
    const response = await this.client.post(`/api/me/bounty/claim`, { claim_code: claimCode })
    return response.data as BountyClaimResult
  }

  // Members — Backend liefert {members: [{member_id, user_id, keycloak_id, ...}]}.
  // Frontend-Code erwartet `id` als Member-PK. Wir mappen member_id → id und
  // reichen keycloak_id mit, damit Mutations auch realm-only-Members
  // (member_id=0) treffen koennen.
  async getMembers(slug: string) {
    const response = await this.client.get(`/api/org/${slug}/members`)
    const raw = response.data?.members || response.data?.users || []
    type RawMember = Record<string, unknown>
    const mapped = (raw as RawMember[]).map((m) => ({
      id: (m['member_id'] as number) || 0,
      keycloak_id: m['keycloak_id'] as string,
      user_id: m['user_id'] as number,
      email: m['email'] as string,
      first_name: m['first_name'] as string | undefined,
      last_name: m['last_name'] as string | undefined,
      role: m['role'] as string,
      status: m['status'] as string,
      account_type: (m['account_type'] as 'person' | 'service' | 'mailbox') ?? 'person',
      enabled: m['enabled'] as boolean | undefined,
    }))
    return { members: mapped, users: mapped }
  }

  async inviteMember(slug: string, email: string, role: 'admin' | 'member', first_name?: string, last_name?: string) {
    const response = await this.client.post(`/api/org/${slug}/members/invite`, { email, role, first_name, last_name })
    return response.data
  }

  async removeMember(slug: string, memberId: string | number) {
    const response = await this.client.delete(`/api/org/${slug}/members/${memberId}`)
    return response.data
  }

  async updateMemberRole(slug: string, memberId: string | number, role: 'admin' | 'member' | 'operator' | 'viewer') {
    const response = await this.client.patch(`/api/org/${slug}/members/${memberId}`, { role })
    return response.data
  }

  // Member-Detail (Cockpit Drawer)
  async getMemberDetail(slug: string, ref: string | number) {
    const response = await this.client.get(`/api/org/${slug}/members/${ref}/detail`)
    return response.data?.data
  }

  async listOrgGroups(slug: string) {
    const response = await this.client.get(`/api/org/${slug}/groups`)
    return response.data?.groups ?? []
  }

  async addMemberToGroup(slug: string, ref: string | number, groupId: string) {
    const response = await this.client.put(`/api/org/${slug}/members/${ref}/groups/${groupId}`)
    return response.data
  }

  async removeMemberFromGroup(slug: string, ref: string | number, groupId: string) {
    const response = await this.client.delete(`/api/org/${slug}/members/${ref}/groups/${groupId}`)
    return response.data
  }

  // Services
  async getOrgServices(slug: string) {
    const response = await this.client.get(`/api/org/${slug}/services`)
    return response.data
  }

  // TQ #5711: Org-scoped Live-Health-Probe der gebuchten Dienste + VMs.
  // Backend GET /api/org/:slug/health (org-member read tier, GetOrgHealth in
  // org_invoices_health.go) — gleiche Response-Shape wie /api/my-services/health.
  async getOrgHealth(slug: string): Promise<OrgHealthResponse> {
    const response = await this.client.get(`/api/org/${slug}/health`)
    return response.data as OrgHealthResponse
  }

  // TQ #5453: Onboarding-Wizard Skip + Restart (Owner/Admin only)
  async skipOrgWizard(slug: string): Promise<{ ok: boolean }> {
    const response = await this.client.post(`/api/org/${slug}/wizard/skip`)
    return response.data
  }

  async restartOrgWizard(slug: string): Promise<{ ok: boolean }> {
    const response = await this.client.post(`/api/org/${slug}/wizard/restart`)
    return response.data
  }

  // User Management (Keycloak)
  async getOrgUsers(slug: string) {
    const response = await this.client.get(`/api/org/${slug}/users`)
    return response.data
  }

  async createOrgUser(slug: string, data: {
    username: string
    email: string
    first_name: string
    last_name: string
    temp_password?: string
    send_email: boolean
    role: 'admin' | 'member'
  }) {
    const response = await this.client.post(`/api/org/${slug}/users`, data)
    return response.data
  }

  // Kunden-Instanz (cockpit-api POST /users): kein Temp-Passwort, Keycloak verschickt die Mail mit Link.
  async createTenantUser(slug: string, data: {
    email: string
    firstName: string
    lastName: string
    role: 'admin' | 'operator' | 'viewer'
  }) {
    const response = await this.client.post(`/api/org/${slug}/users`, data)
    return response.data as CreateTenantUserResponse
  }

  async toggleUserEnabled(slug: string, userId: string, enabled: boolean) {
    const response = await this.client.patch(`/api/org/${slug}/users/${userId}/enabled`, { enabled })
    return response.data
  }

  async resetUserPassword(slug: string, userId: string) {
    const response = await this.client.post(`/api/org/${slug}/users/${userId}/reset-password`)
    return response.data
  }

  async getOrgScoutUsage(slug: string): Promise<{ month: string; total_messages: number; users_with_usage: number; message_limit: number }> {
    const response = await this.client.get(`/api/org/${slug}/scout-usage`)
    return response.data.usage
  }

  async requestTokenOverage(slug: string): Promise<{ success: boolean; portal_url: string }> {
    const response = await this.client.post(`/api/org/${slug}/token-overage`)
    return response.data
  }
  private async cortexRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.cortexBaseURL}${endpoint}`
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    const response = await fetch(url, { ...options, headers })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }))
      throw errorData
    }
    if (response.status === 204) {
      return undefined as T
    }
    return response.json()
  }

  // ----- Service-Requests (Kunden-Anfragen-Inbox, TQ #9696, Teil C) --------
  // Nutzt den gemeinsamen staffApiBase/staffRequest-Helper (weiter oben im
  // Staff-API-Block definiert) — /admin/service-requests liegt auf admin-api,
  // NICHT auf portal-api /api/staff/*.

  async staffListPendingRequests() {
    return this.staffRequest<{ requests: StaffServiceRequest[] }>(
      '/admin/service-requests',
    )
  }
  async staffListInProgressRequests() {
    return this.staffRequest<{ requests: StaffServiceRequest[] }>(
      '/admin/service-requests/in-progress',
    )
  }
  async staffListRequestHistory() {
    return this.staffRequest<{ requests: StaffServiceRequest[] }>(
      '/admin/service-requests/history',
    )
  }
  async staffCompleteRequest(id: string, notes?: string) {
    return this.staffRequest(`/admin/service-requests/${id}/complete`, {
      method: 'PATCH',
      body: JSON.stringify({ notes: notes || '' }),
    })
  }
  async staffRejectRequest(id: string, reason: string) {
    return this.staffRequest(`/admin/service-requests/${id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    })
  }

  async cortexChat(message: string, sessionId?: string, mode?: 'scout' | 'aria' | 'onboarding'): Promise<CortexChatResponse> {
    const body: Record<string, unknown> = { message, session_id: sessionId }
    if (mode) body.mode = mode
    return this.cortexRequest<CortexChatResponse>('/api/v1/chat', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  // ── Privacy-Mode (TQ #8466 / DSGVO) ────────────────────────────────────
  // Backend-Vertrag cortex PR #107 (Migration 085): GET liefert den aktuellen
  // Tier; PUT setzt ihn. 'us_ok' OHNE privacy_confirmed:true -> 400 (Consent-
  // Gate, fail-closed). 'local' wird der Kundin NIE als Tier angeboten.
  // TQ #13061: processing_preference wird als Primärweg unterstützt.
  async getPrivacyMode(): Promise<PrivacyModeInfo> {
    return this.cortexRequest<PrivacyModeInfo>('/api/v1/privacy-mode')
  }

  async setPrivacyMode(
    privacyMode: PrivacyMode | null,
    privacyConfirmed = false,
    processingPreference?: ProcessingPreference,
  ): Promise<PrivacyModeInfo> {
    const body: Record<string, unknown> = {
      privacy_confirmed: privacyConfirmed,
    }
    if (privacyMode !== null) {
      body.privacy_mode = privacyMode
    }
    if (processingPreference !== undefined) {
      body.processing_preference = processingPreference
    }
    return this.cortexRequest<PrivacyModeInfo>('/api/v1/privacy-mode', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  // ── Org-Aria-Default: ai_mode (TQ #8504) ───────────────────────────────
  // Duenner Org-Scope-Wrapper fuer die geteilte @it-sce/aria-settings-form. Der
  // Org-Default ai_mode wird als Teil des bestehenden Org-Aria-Settings-Records
  // (/api/org/{slug}/aria-settings) persistiert — KEIN eigener Endpoint, KEINE
  // Personal-/me-Settings-Vermischung (das ist der Org-Default, NICHT der
  // per-User-Override). 'local'|'cloud' = cortex ai_mode CHECK.
  // TQ #320: ai_mode lebt in Cortex (tenant_rate_configs), portal-api proxiet
  // GET/PUT /api/v1/ai-mode 1:1 durch und haengt die rohe Cortex-Antwort unter
  // dem Top-Level-Feld `ai_mode` an (NICHT unter `settings.ai_mode` -- das war
  // der Bug: die alte org_settings-UPSERT-Spaltenliste kannte ai_mode gar
  // nicht, der Schalter meldete 200 und schrieb nichts).
  // TQ #337 (25.09.2026): `avv_erp_status` ist laut Handler (org_aria_settings.go,
  // GetOrgAriaSettings) ein TOP-LEVEL Feld der Response (resp["avv_erp_status"]),
  // Geschwister von `settings` und `ai_mode`. Zeigt ob der AVV in ERPNext
  // signiert vorliegt, fuer den AVV-Hinweis am KI-Modus-Schalter
  // (OrgAiSettingsTab.tsx).
  async getOrgAiMode(slug: string): Promise<{
    ai_mode: 'local' | 'cloud' | null
    avv_confirmed_at: string | null
    avv_erp_status: 'signed' | 'missing' | 'unknown' | null
  }> {
    const resp = await this.client.get(`/api/org/${slug}/aria-settings`)
    const cortex = resp.data?.ai_mode as { ai_mode?: string | null; avv_confirmed_at?: string | null } | undefined
    const raw = cortex?.ai_mode
    const ai_mode = raw === 'local' || raw === 'cloud' ? raw : null
    const rawAvv = resp.data?.avv_erp_status as string | undefined
    const avv_erp_status = rawAvv === 'signed' || rawAvv === 'missing' || rawAvv === 'unknown' ? rawAvv : null
    return { ai_mode, avv_confirmed_at: cortex?.avv_confirmed_at ?? null, avv_erp_status }
  }

  // avvConfirmed muss true sein, wenn aiMode 'cloud' ist -- Cortex lehnt sonst
  // mit 400 ab (AVV = Auftragsverarbeitungsvertrag, DSGVO-Zustimmung fuer
  // Cloud-KI). Fuer 'local' wird das Feld ignoriert.
  async setOrgAiMode(
    slug: string,
    aiMode: 'local' | 'cloud' | null,
    avvConfirmed = false,
  ): Promise<{ ai_mode: 'local' | 'cloud' | null; avv_confirmed_at: string | null }> {
    const resp = await this.client.put(`/api/org/${slug}/aria-settings`, {
      ai_mode: aiMode,
      avv_confirmed: avvConfirmed,
    })
    const cortex = resp.data?.ai_mode as { ai_mode?: string | null; avv_confirmed_at?: string | null } | undefined
    const raw = cortex?.ai_mode
    return {
      ai_mode: raw === 'local' || raw === 'cloud' ? raw : aiMode,
      avv_confirmed_at: cortex?.avv_confirmed_at ?? null,
    }
  }

  // ── Confirm-Engine (TQ #6981 / Confirm-Engine P3) ──────────────────────
  // Wird aus der Confirm-Card aufgerufen, wenn ein `confirmation_required`
  // Stream-Event empfangen wurde. approve fuehrt den geparkten Tool-Call aus
  // und liefert das Tool-Result zurueck; cancel verwirft ihn.

  async approveCortexConfirmation(confirmId: string): Promise<CortexConfirmationResult> {
    return this.cortexRequest<CortexConfirmationResult>(
      `/api/v1/confirmations/${encodeURIComponent(confirmId)}/approve`,
      { method: 'POST' },
    )
  }

  async cancelCortexConfirmation(confirmId: string): Promise<CortexConfirmationResult> {
    return this.cortexRequest<CortexConfirmationResult>(
      `/api/v1/confirmations/${encodeURIComponent(confirmId)}/cancel`,
      { method: 'POST' },
    )
  }

  /**
   * TQ #6093: WebSocket streaming chat. Liefert event-by-event Updates
   * (content delta, tool_start, tool_result, done) so dass die UI einen
   * Status-Indikator anzeigen kann statt mehrerer leerer Avatar-Bubbles.
   *
   * Auth: Browser-WebSocket-API kann keine `Authorization` Header setzen —
   * Cortex akzeptiert deshalb das JWT auch via Sec-WebSocket-Protocol
   * Subprotokoll ("bearer, <jwt>"). Browser fordert das Subprotokoll an,
   * Server echoed es zurueck.
   *
   * Returns ein abort() um den Stream vorzeitig zu schliessen.
   */
  startCortexChatStream(opts: {
    message: string
    sessionId?: string
    model?: string
    onEvent: (e: CortexStreamEvent) => void
    onClose: (clean: boolean) => void
  }): { abort: () => void } {
    // wss:// fuer Production (https), ws:// fuer Vite-Dev (http) — beides
    // gegen this.cortexBaseURL, per-Tenant aufgeloest via CortexHostAPI
    // (main.tsx), Default https://cortex.it-sce.de bis die Aufloesung durch ist.
    // TQ #409: Pfad-Instanz hat einen relativen cortexBaseURL (/<kennung>/cortex) -> Pfad behalten.
    const wsUrl = cortexStreamUrl(this.cortexBaseURL, window.location.href)
    const ws = new WebSocket(wsUrl, ['bearer', this.token])

    ws.onopen = () => {
      ws.send(JSON.stringify({
        message: opts.message,
        session_id: opts.sessionId,
        model: opts.model,
      }))
    }
    ws.onmessage = (evt) => {
      try {
        opts.onEvent(JSON.parse(evt.data as string) as CortexStreamEvent)
      } catch {
        // malformed frame, skip
      }
    }
    ws.onclose = (evt) => {
      opts.onClose(evt.wasClean)
    }
    ws.onerror = () => {
      // onclose folgt direkt danach — opts.onClose dort behandelt
    }
    return {
      abort: () => {
        try { ws.close() } catch { /* idempotent */ }
      },
    }
  }

  // TQ #5325: Scout-Onboarding-Wizard persistence (org-admin only).
  // soul_addendum is the markdown the wizard produced after dialog with
  // Aria; it gets written to cortex-db.org_settings.soul_addendum and
  // becomes the tenant's permanent Aria-Persona-Addendum.
  async putOrgSoulAddendum(slugOrToken: string, soulAddendum: string): Promise<void> {
    await this.client.post(`/api/org/${encodeURIComponent(slugOrToken)}/settings/soul-addendum`, {
      soul_addendum: soulAddendum,
    })
  }

  async getOrgSoulAddendum(slugOrToken: string): Promise<{ soul_addendum: string; filled: boolean }> {
    const response = await this.client.get(`/api/org/${encodeURIComponent(slugOrToken)}/settings/soul-addendum`)
    return response.data
  }

  async listCortexSessions(opts: { includeArchived?: boolean; limit?: number; offset?: number } = {}): Promise<CortexSession[]> {
    const params = new URLSearchParams()
    if (opts.includeArchived) params.set('include_archived', 'true')
    if (opts.limit != null) params.set('limit', String(opts.limit))
    if (opts.offset != null) params.set('offset', String(opts.offset))
    const qs = params.toString()
    const endpoint = `/api/v1/sessions${qs ? `?${qs}` : ''}`
    const data = await this.cortexRequest<CortexSession[] | { sessions?: CortexSession[]; data?: CortexSession[] }>(endpoint)
    if (Array.isArray(data)) return data
    return data.sessions || data.data || []
  }

  async createCortexSession(input: { title?: string; model?: string; agent_id?: string } = {}): Promise<CortexSession> {
    return this.cortexRequest<CortexSession>('/api/v1/sessions', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async renameCortexSession(sessionId: string, title: string): Promise<CortexSession> {
    return this.cortexRequest<CortexSession>(`/api/v1/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    })
  }

  async deleteCortexSession(sessionId: string): Promise<void> {
    await this.cortexRequest<{ deleted: boolean }>(`/api/v1/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    })
  }

  async listCortexModels(): Promise<CortexModelInfo[]> {
    const data = await this.cortexRequest<CortexModelInfo[] | { models?: CortexModelInfo[]; data?: CortexModelInfo[] }>('/api/v1/models')
    if (Array.isArray(data)) return data
    return data.models || data.data || []
  }

  async getCortexMcpStatus(tenant?: string): Promise<CortexMcpStatus> {
    // tenant-Override: Admins können explizit den Tenant der gerade
    // angezeigten Cockpit-Org mitschicken (statt des eigenen JWT-Tenants).
    // Non-Admins werden serverseitig auf ihren eigenen Tenant zurückgezwungen.
    const path = tenant
      ? `/api/v1/mcp/status?tenant=${encodeURIComponent(tenant)}`
      : '/api/v1/mcp/status'
    return this.cortexRequest<CortexMcpStatus>(path)
  }

  async getCortexMeSettings(): Promise<CortexMeSettings> {
    return this.cortexRequest<CortexMeSettings>('/api/v1/me/settings')
  }

  async updateCortexMeSettings(input: { preferred_model?: string | null; ai_mode?: string | null }): Promise<CortexMeSettings> {
    return this.cortexRequest<CortexMeSettings>('/api/v1/me/settings', {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  }

  async listTenantCredentials(tenant: string): Promise<CortexCredentialsListResponse> {
    return this.cortexRequest<CortexCredentialsListResponse>(
      `/api/v1/admin/tenants/${encodeURIComponent(tenant)}/credentials`,
    )
  }

  async setTenantCredentials(
    tenant: string,
    service: string,
    credentials: Record<string, CortexCredentialUpsert>,
  ): Promise<{ tenant: string; service_type: string; kinds_updated: string[] }> {
    return this.cortexRequest(
      `/api/v1/admin/tenants/${encodeURIComponent(tenant)}/credentials/${encodeURIComponent(service)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ credentials }),
      },
    )
  }

  async deleteTenantCredentials(
    tenant: string,
    service: string,
  ): Promise<{ tenant: string; service_type: string; rows_deleted: number; grant_deleted: boolean }> {
    return this.cortexRequest(
      `/api/v1/admin/tenants/${encodeURIComponent(tenant)}/credentials/${encodeURIComponent(service)}`,
      { method: 'DELETE' },
    )
  }

  /// Live health-check against the real service backend (IMAP login, WebDAV
  /// PROPFIND, ERPNext API ping). Returns `ok=true` only if the stored
  /// credentials actually authenticate. See `api/credentials_test.rs`.
  async testTenantCredentials(
    tenant: string,
    service: string,
  ): Promise<{ ok: boolean; message: string; duration_ms: number; tenant: string; service: string }> {
    return this.cortexRequest(
      `/api/v1/admin/tenants/${encodeURIComponent(tenant)}/credentials/${encodeURIComponent(service)}/test`,
      { method: 'POST' },
    )
  }

  // ── User-level (personal-delegate) credentials ──────────────────────────
  // Bound to the JWT-authenticated user; Cortex prefers these over tenant
  // credentials when an MCP tool is invoked. UI for "Aria-Vollmacht für
  // mein eigenes Postfach/Cloud".

  async listUserCredentials(): Promise<CortexUserSidecarSummary[]> {
    return this.cortexRequest<CortexUserSidecarSummary[]>('/api/v1/credentials')
  }

  async getUserCredentials(sidecar: string): Promise<CortexUserSidecarKeyInfo> {
    return this.cortexRequest<CortexUserSidecarKeyInfo>(
      `/api/v1/credentials/${encodeURIComponent(sidecar)}`,
    )
  }

  async setUserCredentials(
    sidecar: string,
    credentials: Record<string, string>,
  ): Promise<{ status: string; sidecar: string; keys_set: number }> {
    return this.cortexRequest(
      `/api/v1/credentials/${encodeURIComponent(sidecar)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ credentials }),
      },
    )
  }

  async deleteUserCredentials(
    sidecar: string,
  ): Promise<{ status: string; deleted: boolean; sidecar: string }> {
    return this.cortexRequest(
      `/api/v1/credentials/${encodeURIComponent(sidecar)}`,
      { method: 'DELETE' },
    )
  }

  // ── Admin Standing-Permissions (TQ #6984 / Confirm-Engine P4) ───────────
  // Org-Admin verwaltet die standing_permissions eines Mitglieds. userId ist
  // die Keycloak-/Cortex-User-ID (= MemberDetail.keycloak_id).

  async listUserGrants(userId: string): Promise<CortexStandingPermission[]> {
    const data = await this.cortexRequest<
      CortexStandingPermission[] | { permissions?: CortexStandingPermission[]; data?: CortexStandingPermission[] }
    >(`/api/v1/admin/users/${encodeURIComponent(userId)}/permissions`)
    if (Array.isArray(data)) return data
    return data.permissions || data.data || []
  }

  async grantUserPermission(userId: string, input: CortexGrantInput): Promise<CortexStandingPermission> {
    return this.cortexRequest<CortexStandingPermission>(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/permissions`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  }

  async revokeUserGrant(userId: string, permissionId: string): Promise<void> {
    await this.cortexRequest<void>(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/permissions/${encodeURIComponent(permissionId)}`,
      { method: 'DELETE' },
    )
  }

  async getCortexSessionMessages(sessionId: string): Promise<CortexSessionMessage[] | null> {
    try {
      const response = await this.cortexRequest<
        CortexSessionMessage[] | { messages?: CortexSessionMessage[]; data?: CortexSessionMessage[] }
      >(`/api/v1/sessions/${encodeURIComponent(sessionId)}/messages`)
      if (Array.isArray(response)) return response
      return response.messages || response.data || []
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'error' in err
          ? String((err as { error: string }).error)
          : ''
      if (status.includes('404')) {
        return null
      }
      throw err
    }
  }
}

export const api = new ApiService()
